import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../App.css';
import SDSTemplate from './SDSTemplate';
import SDSEditorPanel from './SDSEditorPanel';
import { initialSDSData, buildInitialSDSData, STORAGE_KEY } from '../data/initialData';
import { Button } from './ui/button';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from './ui/alert-dialog';
import { Download, Image as ImageIcon, RotateCcw, FileText, ZoomIn, ZoomOut, FileType2, Eye, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import { exportToWord } from '../utils/wordExport';

function App({ onAfterPreview } = {}) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const dir = isEn ? 'ltr' : 'rtl';

  const [data, setData] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with initial data to ensure new fields (like facility) exist
        return { ...initialSDSData, ...parsed, facility: { ...initialSDSData.facility, ...(parsed.facility || {}) } };
      }
    } catch (e) {
      // Saved data is corrupted JSON or localStorage is unavailable.
      // Surface for debugging but fall through to language-aware defaults.
      console.warn('[SDSEditor] could not parse saved data, using defaults', e);
    }
    // First-load with no saved data: use language-aware defaults from i18n
    return buildInitialSDSData(t);
  });
  const [zoom, setZoom] = useState(0.78);
  const [busy, setBusy] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  // Preview state: holds both blob URL (for download) and data URI (for embed display)
  const [pdfPreview, setPdfPreview] = useState(null); // { blobUrl, dataUri }
  const wrapRef = useRef(null);

  // Listen for global "request preview" events (used by SDSEditorPage after Save)
  useEffect(() => {
    const handler = () => { handlePreviewPDF(); };
    window.addEventListener('sds:preview-pdf', handler);
    return () => window.removeEventListener('sds:preview-pdf', handler);
  }, []);

  useEffect(() => () => {
    if (pdfPreview?.blobUrl) URL.revokeObjectURL(pdfPreview.blobUrl);
  }, [pdfPreview]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // QuotaExceededError or localStorage disabled — log so we can spot it.
      console.warn('[SDSEditor] failed to persist data', e);
    }
  }, [data]);

  const handleReset = () => setResetConfirmOpen(true);
  const performReset = () => {
    setData(buildInitialSDSData(t));
    toast.success(t('sds.resetSuccess'));
  };

  const captureDataURL = async (opts = {}) => {
    const node = document.getElementById('sds-template');
    if (!node) return null;
    // Goal: produce a PNG with the EXACT A4 portrait aspect ratio (1100 × 1556)
    // WITHOUT distorting the content. We use a UNIFORM scale() transform so the
    // page never looks stretched / squished. If content is shorter than A4, we
    // pad the bottom with white; if it is taller, we shrink uniformly to fit
    // (with side margins). This eliminates the visible "fold" artifact caused
    // by the previous non-uniform scaleY approach.
    const TARGET_W = 1100;
    const TARGET_H = 1556;

    const original = {
      transform: node.style.transform,
      transformOrigin: node.style.transformOrigin,
      height: node.style.height,
      width: node.style.width,
      boxShadow: node.style.boxShadow,
    };

    // Disable the drop shadow during capture — it can render unwanted gradient
    // bands into the PNG depending on the device pixel ratio.
    node.style.boxShadow = 'none';

    const naturalW = TARGET_W; // template width is locked at 1100
    const naturalH = Math.max(node.scrollHeight, node.offsetHeight, 1);

    // Uniform scale factor: shrink only when content is too tall.
    const scale = naturalH > TARGET_H ? TARGET_H / naturalH : 1;
    const scaledW = naturalW * scale;
    const scaledH = naturalH * scale;

    // Center the scaled content horizontally inside the A4 canvas (in RTL the
    // origin stays top-right so layout is preserved — the canvas is wider than
    // the scaled content only when we had to shrink vertically).
    const offsetX = Math.max(0, (TARGET_W - scaledW) / 2);

    node.style.transformOrigin = 'top left';
    node.style.transform = `translateX(${offsetX}px) scale(${scale})`;
    node.style.width = `${naturalW}px`;
    node.style.height = `${naturalH}px`;

    try {
      const dataUrl = await toPng(node, {
        pixelRatio: opts.pixelRatio ?? 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
        skipFonts: false,
        fetchRequestInit: { mode: 'cors' },
        // Canvas is always exactly A4 portrait. Any unused vertical space
        // (when content is shorter than A4) becomes a clean white margin.
        width: TARGET_W,
        height: TARGET_H,
        style: {
          transform: node.style.transform,
          transformOrigin: 'top left',
        },
      });
      return dataUrl;
    } finally {
      node.style.transform = original.transform;
      node.style.transformOrigin = original.transformOrigin;
      node.style.height = original.height;
      node.style.width = original.width;
      node.style.boxShadow = original.boxShadow;
    }
  };

  // Expose capture function globally so SDSEditorPage can generate thumbnails after save.
  useEffect(() => {
    window.__sdsCapture = captureDataURL;
    window.__sdsBuildPdfBlob = async () => {
      const pdf = await buildPdf();
      if (!pdf) return null;
      return pdf.output('blob');
    };
    return () => {
      // Best-effort cleanup of the test/automation hooks. `delete` on window
      // can throw under strict mode in some browsers — swallow safely.
      try { delete window.__sdsCapture; } catch (e) { console.debug('cleanup __sdsCapture failed', e); }
      try { delete window.__sdsBuildPdfBlob; } catch (e) { console.debug('cleanup __sdsBuildPdfBlob failed', e); }
    };
  }, []);

  const safeFactoryName = () => {
    const raw = (data?.facility?.name || 'SDS-Report').toString().trim() || 'SDS-Report';
    // Replace spaces and forbidden filename chars; keep arabic letters and digits.
    return raw.replace(/[\\\/:*?"<>|]+/g, '').replace(/\s+/g, '_');
  };

  const handleDownloadImage = async () => {
    setBusy(true);
    toast.info(t('sds.preparingImage'));
    try {
      const dataUrl = await captureDataURL();
      if (!dataUrl) return;
      const link = document.createElement('a');
      link.download = `${safeFactoryName()}_SDS.png`;
      link.href = dataUrl;
      link.click();
      toast.success(t('sds.imageSaved'));
    } catch (e) {
      toast.error(t('sds.imageError'));
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const buildPdf = async () => {
    const dataUrl = await captureDataURL();
    if (!dataUrl) return null;
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pdfWidth = pdf.internal.pageSize.getWidth();    // 210
    const pdfHeight = pdf.internal.pageSize.getHeight();  // 297
    // captureDataURL always returns a snapshot at the exact A4 portrait aspect ratio
    // (1100×1556), so we can drop it straight onto the page edge-to-edge.
    pdf.addImage(dataUrl, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
    return pdf;
  };

  const handlePreviewPDF = async () => {
    setBusy(true);
    toast.info(t('sds.preparingPreview'));
    try {
      const pdf = await buildPdf();
      if (!pdf) return;
      const blob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(blob);
      const dataUri = pdf.output('datauristring');
      setPdfPreview({ blobUrl, dataUri });
      toast.success(t('sds.previewReady'));
      if (typeof onAfterPreview === 'function') onAfterPreview();
    } catch (e) {
      toast.error(t('sds.previewError'));
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadPDF = async () => {
    setBusy(true);
    toast.info(t('sds.preparingPdf'));
    try {
      const pdf = await buildPdf();
      if (!pdf) return;
      pdf.save(`${safeFactoryName()}_SDS.pdf`);
      toast.success(t('sds.pdfSaved'));
    } catch (e) {
      toast.error(t('sds.pdfError'));
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadWord = () => {
    setBusy(true);
    toast.info(t('sds.preparingWord'));
    try {
      exportToWord(data);
      toast.success(t('sds.wordSaved'));
    } catch (e) {
      toast.error(t('sds.wordError'));
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App min-h-screen bg-slate-100">
      {/* PDF Preview Overlay (in-browser, full-screen) */}
      {pdfPreview && (
        <div className="fixed inset-0 z-[100] bg-slate-900 flex flex-col" dir={dir} data-testid="pdf-preview-overlay">
          <div className="bg-white border-b shadow px-4 py-2 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => {
                  if (pdfPreview.blobUrl) URL.revokeObjectURL(pdfPreview.blobUrl);
                  setPdfPreview(null);
                }}
                className="bg-[#1a4d80] hover:bg-[#143d68] text-white"
                data-testid="pdf-preview-back-btn"
              >
                <ArrowRight size={16} className="ml-1" /> {t('sds.backToEdit')}
              </Button>
              <div className="text-sm font-bold text-slate-800 truncate max-w-md">
                <FileText size={14} className="inline ml-1 text-[#1a4d80]"/>
                {t('sds.previewTitle')} — {data?.facility?.name || t('sds.editorTitle')}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = pdfPreview.blobUrl;
                  a.download = `${safeFactoryName()}_SDS.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                }}
                className="text-xs"
                data-testid="pdf-preview-download-btn"
              >
                <Download size={14} className="ml-1" /> {t('sds.download')}
              </Button>
            </div>
          </div>
          <div className="flex-1 w-full bg-slate-300 relative">
            <object
              data={pdfPreview.dataUri}
              type="application/pdf"
              className="w-full h-full"
              data-testid="pdf-preview-iframe"
            >
              <embed
                src={pdfPreview.dataUri}
                type="application/pdf"
                className="w-full h-full"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-white">
                <div className="max-w-md text-center p-6">
                  <FileText className="mx-auto text-[#1a4d80] mb-3" size={48}/>
                  <h3 className="font-bold text-lg mb-2">{t('sds.pdfBlockedTitle')}</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    {t('sds.pdfBlockedBody')}
                  </p>
                  <Button
                    onClick={() => {
                      const a = document.createElement('a');
                      a.href = pdfPreview.blobUrl;
                      a.download = `${safeFactoryName()}_SDS.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                    }}
                    className="bg-[#1a4d80] hover:bg-[#143d68] text-white"
                  >
                    <Download size={14} className="ml-1"/> {t('sds.pdfDownloadFallback')}
                  </Button>
                </div>
              </div>
            </object>
          </div>
        </div>
      )}
      {/* Top bar */}
      <header className="no-print sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1800px] mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3" dir={dir}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1a4d80] flex items-center justify-center text-white">
              <FileText size={22} />
            </div>
            <div>
              <h1 className="text-base font-extrabold text-slate-900 leading-tight">
                {t('sds.editorTitle')}
              </h1>
              <p className="text-[11px] text-slate-500">{t('sds.editorSubtitle')}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1">
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                onClick={() => setZoom((z) => Math.max(0.4, z - 0.05))}
              >
                <ZoomOut size={14} />
              </Button>
              <span className="text-xs font-semibold w-12 text-center text-slate-700">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                onClick={() => setZoom((z) => Math.min(1.2, z + 0.05))}
              >
                <ZoomIn size={14} />
              </Button>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={busy}
              className="text-xs"
              data-testid="sds-reset-btn"
            >
              <RotateCcw size={14} className="ml-1" /> {t('sds.reset')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadImage}
              disabled={busy}
              className="text-xs"
            >
              <ImageIcon size={14} className="ml-1" /> {t('sds.saveImage')}
            </Button>
            <Button
              size="sm"
              onClick={handleDownloadPDF}
              disabled={busy}
              className="text-xs bg-[#1a4d80] hover:bg-[#143d68]"
              data-testid="download-pdf-btn"
            >
              <Download size={14} className="ml-1" /> {t('sds.exportPdf')}
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-[1800px] mx-auto px-4 py-5">
        <div className="grid grid-cols-12 gap-4" dir={dir}>
          {/* Editor panel */}
          <aside className="no-print col-span-12 lg:col-span-3 xl:col-span-3">
            <SDSEditorPanel data={data} setData={setData} />
          </aside>

          {/* Template preview */}
          <section className="col-span-12 lg:col-span-9 xl:col-span-9">
            <div
              ref={wrapRef}
              className="flex justify-center items-start overflow-auto bg-slate-200 rounded-lg p-4"
              style={{ minHeight: '80vh' }}
            >
              <div
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top center',
                  width: 1100,
                }}
              >
                <SDSTemplate data={data} setData={setData} />
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Reset confirmation (replaces native window.confirm) */}
      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent dir={dir} data-testid="sds-reset-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sds.reset')}</AlertDialogTitle>
            <AlertDialogDescription>{t('sds.resetConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sds-reset-cancel">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="sds-reset-ok"
              onClick={() => { setResetConfirmOpen(false); performReset(); }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default App;
